import React, { type HTMLAttributes, useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import type { ToTransientProps } from 'types/styled';
import { v4 as uuidv4 } from 'uuid';

type SpinnerProps = HTMLAttributes<HTMLOrSVGElement> & {
    variant?: 'default' | 'negative';
    size?: number;
    strokeWidth?: number;
    animationDuration?: number;
    timeout?: boolean | number;
    helperText?: string;
};

const rotateAnimation = keyframes`
    from {transform: rotate(0deg);}
    to {transform: rotate(360deg);}
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.Spacing8};
`;

const Svg = styled.svg<ToTransientProps<Pick<SpinnerProps, 'animationDuration' | 'variant'>>>`
    transition-property: transform;
    animation-name: ${rotateAnimation};
    animation-duration: ${({ $animationDuration }) => $animationDuration}ms;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    color: ${({ theme, $variant }) => ($variant === 'negative' ? theme.ColorIconBrandInverted : theme.ColorIconBrand)};
`;

const HelperText = styled.div<ToTransientProps<Pick<SpinnerProps, 'variant'>>>`
    font-size: ${({ theme }) => theme.TypographyBodySizeSmall};
    line-height: 150%;
    color: ${({ theme, $variant }) =>
        $variant === 'negative' ? theme.ColorTextSecondaryInverted : theme.ColorTextSecondary};
`;

const defaults: Required<Pick<SpinnerProps, 'size' | 'strokeWidth' | 'animationDuration'>> = {
    size: 24,
    strokeWidth: 2,
    animationDuration: 1000
};

export default function Loader(props: SpinnerProps): JSX.Element {
    const {
        size = defaults.size,
        strokeWidth = defaults.strokeWidth,
        animationDuration = defaults.animationDuration,
        timeout,
        className,
        variant = 'default',
        helperText
    } = props;
    const cutOff = uuidv4();
    const gradient = uuidv4();
    const [showSpinner, setShowSpinner] = useState(!timeout);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;
        const timeoutDuration = typeof timeout === 'number' ? timeout : 100;

        if (timeout) {
            timer = setTimeout(() => setShowSpinner(true), timeoutDuration);
        }

        return () => {
            if (timeout) {
                clearTimeout(timer);
            }
        };
    }, [timeout]);

    if (!showSpinner) {
        return <div css={{ width: size, height: size }} className={className} />;
    }

    return (
        <Wrapper className={className}>
            <Svg width={size} height={size} $animationDuration={animationDuration} $variant={variant}>
                <defs>
                    <clipPath id={cutOff}>
                        <rect x="0" y={size / 2} width={size} height={size} />
                    </clipPath>
                    <linearGradient id={gradient}>
                        <stop offset="0" stopColor="currentColor" />
                        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                    </linearGradient>
                </defs>

                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={size / 2 - strokeWidth}
                    stroke={`url(#${gradient})`}
                    strokeWidth={strokeWidth}
                    fill="none"
                    clipPath={`url(#${cutOff})`}
                />
            </Svg>
            {helperText && <HelperText $variant={variant}>{helperText}</HelperText>}
        </Wrapper>
    );
}
